import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import { useRbacReadiness } from "@/hooks/useRbacReadiness";
import type { Permission } from "@/constants/permissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { logger } from "@/utils/logger";
import { TosConsentGate } from "@/components/auth/TosConsentGate";
import { AccessReadiness } from "@/components/auth/AccessReadiness";

interface PermissionProtectedRouteProps {
  permission: Permission;
  children: ReactNode;
}

/**
 * Route guard for a customer feature gated on an org permission.
 *
 * Wait for successful current-org permissions and assignments before deciding
 * whether access is denied. A failed or stalled load stays recoverable here.
 */
const PermissionProtectedRoute = observer(function PermissionProtectedRoute({
  permission,
  children,
}: PermissionProtectedRouteProps) {
  const location = useLocation();
  const { t } = useLanguage();
  const { ready, failed, retry, orgId, noOrganization } = useRbacReadiness();
  const { hasPermission } = usePermissions();

  if (noOrganization) return <Navigate to="/" replace />;
  if (!ready) {
    return (
      <AccessReadiness
        failed={failed}
        retry={retry}
        message={t.auth.permissionLoadFailed}
        loadingLabel={t.auth.checkingAccess}
        fullHeight
      />
    );
  }

  if (!hasPermission(permission)) {
    logger.warn(
      "auth",
      "unauthorized-route-access",
      "User without the required permission attempted to access a guarded route",
      { path: location.pathname, permission, orgId }
    );
    toast.error(t.auth.insufficientPrivileges);
    return <Navigate to="/" replace />;
  }

  return <TosConsentGate>{children}</TosConsentGate>;
});

export default PermissionProtectedRoute;
